import { useState, useEffect, lazy, Suspense } from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { PageHeader, Card, LoadingSpinner, StatusBadge } from '../components/Layout';
import { api } from '../services/api';
import { WorkstationQueue, WorkOrderQueueItem, WorkOrderStatus, BlockerType } from '../types';

const ProductionCalendar = lazy(() => import('../components/ProductionCalendar'));
const ProductionQueue = lazy(() => import('../components/ProductionQueue'));

interface OrderInProduction {
  id: string;
  orderNumber: string;
  sidemark: string;
  companyName: string;
  status: string;
  createdAt: string;
  totalItems: number;
  completedItems: number;
  totalWorkOrders: number;
  completedWorkOrders: number;
  inProgressWorkOrders: number;
  progress: number;
}

// Workstation filter names are derived dynamically from loaded queue data

const BLOCKER_TYPES: { value: BlockerType; label: string }[] = [
  { value: 'deposit', label: 'Waiting for Deposit' },
  { value: 'parts_shortage', label: 'Parts Shortage' },
  { value: 'manual_hold', label: 'Manual Hold' },
  { value: 'supplier_delay', label: 'Supplier Delay' },
  { value: 'customer_change', label: 'Customer Change' },
  { value: 'quality_issue', label: 'Quality Issue' },
];

interface ShortageItem {
  partNumber: string;
  description: string;
  qtyNeeded: number;
  qtyShort: number;
  leadTime?: string;
}

interface ShortageOrder {
  orderId: string;
  orderNumber: string;
  dealer: string;
  sidemark: string;
  holdSince: string;
  missingItems: ShortageItem[];
}

export default function ProductionPage() {
  const [queues, setQueues] = useState<WorkstationQueue[]>([]);
  const [ordersInProduction, setOrdersInProduction] = useState<OrderInProduction[]>([]);
  const [shortageOrders, setShortageOrders] = useState<ShortageOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [shortageLoading, setShortageLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'orders' | 'queue' | 'queues' | 'shortages' | 'calendar'>('orders');
  const [selectedWorkstation, setSelectedWorkstation] = useState<string>('');
  const [selectedItem, setSelectedItem] = useState<WorkOrderQueueItem | null>(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockReason, setBlockReason] = useState<BlockerType>('manual_hold');
  const [blockNote, setBlockNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [resolvingOrderId, setResolvingOrderId] = useState<string | null>(null);
  const [expandedShortageOrder, setExpandedShortageOrder] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadShortageData = async () => {
    try {
      setShortageLoading(true);
      const data = await api.production.getShortageReport();
      setShortageOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load shortage report:', err);
      setShortageOrders([]);
    } finally {
      setShortageLoading(false);
    }
  };

  const handleResolveShortage = async (orderId: string) => {
    try {
      setResolvingOrderId(orderId);
      await api.production.resolveShortage(orderId);
      setShortageOrders((prev) => prev.filter((o) => o.orderId !== orderId));
    } catch (err) {
      console.error('Failed to resolve shortage:', err);
    } finally {
      setResolvingOrderId(null);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [queuesData, ordersData] = await Promise.all([
        api.production.getWorkstationQueues().catch(() => []),
        api.production.getOrdersInProduction().catch(() => []),
      ]);
      // Also load shortage data in the background
      loadShortageData();
      // Map API response to expected format
      const mappedQueues = (queuesData || []).map((q: any) => ({
        workstationId: q.id,
        workstationName: q.name,
        items: (q.workOrders || []).map((wo: any) => ({
          workOrderId: wo.id,
          workOrderNumber: wo.workOrderNumber || `WO-${wo.id?.slice(0,8)}`,
          orderNumber: wo.orderNumber,
          customerName: wo.companyName || wo.customerName || 'Unknown',
          sidemark: wo.sidemark,
          stepName: q.name,
          status: wo.status === 'ready' ? 'ready_to_start' : wo.status,
          priority: wo.priority || 1,
          dueDate: wo.dueDate,
          productInfo: {
            material: wo.fabricType,
            color: wo.fabricColor,
            width: wo.maxWidth,
            height: wo.maxHeight,
          },
        })),
        stats: {
          comingUp: q.counts?.pending || 0,
          readyToStart: q.counts?.ready || 0,
          inProgress: q.counts?.inProgress || 0,
          blocked: q.counts?.blocked || 0,
        },
      }));
      setQueues(mappedQueues);
      setOrdersInProduction(ordersData || []);
    } catch (error) {
      console.error('Failed to load production data:', error);
      setQueues([]);
      setOrdersInProduction([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStartWork = async (item: WorkOrderQueueItem) => {
    try {
      setActionLoading(true);
      await api.production.updateWorkOrderStatus(item.workOrderId, 'in_progress');
      await loadData();
    } catch (error) {
      console.error('Failed to start work:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCompleteStep = async (item: WorkOrderQueueItem) => {
    try {
      setActionLoading(true);
      await api.production.completeWorkstationStep(item.workOrderId, item.stepName);
      await loadData();
    } catch (error) {
      console.error('Failed to complete step:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleBlockItem = async () => {
    if (!selectedItem) return;
    try {
      setActionLoading(true);
      await api.production.blockWorkOrder(selectedItem.workOrderId, blockReason, blockNote);
      setShowBlockModal(false);
      setSelectedItem(null);
      setBlockNote('');
      await loadData();
    } catch (error) {
      console.error('Failed to block item:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnblock = async (item: WorkOrderQueueItem) => {
    try {
      setActionLoading(true);
      await api.production.unblockWorkOrder(item.workOrderId);
      await loadData();
    } catch (error) {
      console.error('Failed to unblock item:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrintLabel = async (item: WorkOrderQueueItem) => {
    try {
      const blob = await api.labels.generateLabel(item.workOrderId, item.stepName);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Failed to print label:', error);
    }
  };

  const filteredQueues = selectedWorkstation
    ? queues.filter(q => q.workstationName === selectedWorkstation)
    : queues;

  return (
    <div>
      <PageHeader
        title="Production"
        subtitle="Manage orders in production and workstation queues"
      />

      {/* Tab Navigation */}
      <Card className="mb-6">
        <div className="flex border-b border-gray-200 mb-4">
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'orders'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Orders in Production ({ordersInProduction.length})
          </button>
          <button
            onClick={() => setActiveTab('queue')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'queue'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              Queue
            </span>
          </button>
          <button
            onClick={() => setActiveTab('queues')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'queues'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Workstation Queues ({queues.length})
          </button>
          <button
            onClick={() => {
              setActiveTab('shortages');
              loadShortageData();
            }}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'shortages'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-1.5">
              Shortage Report
              {shortageOrders.length > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
                  {shortageOrders.length}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'calendar'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Production Calendar
            </span>
          </button>
        </div>

        {/* Workstation Filter (only shown for queues tab) */}
        {activeTab === 'queues' && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedWorkstation('')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedWorkstation === ''
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              All Workstations
            </button>
            {queues.map(q => (
              <button
                key={q.workstationId}
                onClick={() => setSelectedWorkstation(q.workstationName)}
                className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                  selectedWorkstation === q.workstationName
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {q.workstationName}
                {(q.stats.inProgress > 0 || q.stats.readyToStart > 0) && (
                  <span className="ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
                    {q.stats.inProgress + q.stats.readyToStart}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </Card>

      {activeTab === 'queue' ? (
        <Card>
          <Suspense fallback={<div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>}>
            <ProductionQueue />
          </Suspense>
        </Card>
      ) : activeTab === 'calendar' ? (
        <Card>
          <Suspense fallback={<div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>}>
            <ProductionCalendar />
          </Suspense>
        </Card>
      ) : loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : activeTab === 'shortages' ? (
        /* Shortage Report Section */
        <Card>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Orders on Shortage Hold</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Orders waiting due to parts shortages. Resolve shortages once parts are available.
              </p>
            </div>
          </div>

          {shortageLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="md" />
            </div>
          ) : shortageOrders.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-12 h-12 mx-auto text-green-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-gray-500 dark:text-gray-400 text-lg">No shortage holds</p>
              <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">All orders have sufficient parts available.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {shortageOrders.map((order) => (
                <div
                  key={order.orderId}
                  className="border border-red-200 dark:border-red-800 rounded-lg overflow-hidden bg-red-50 dark:bg-red-900/10"
                >
                  {/* Order Header Row */}
                  <div className="px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-6 flex-1 min-w-0">
                      <div>
                        <Link
                          to={`/orders/${order.orderId}`}
                          className="text-sm font-semibold text-blue-600 hover:text-blue-800 dark:text-blue-400"
                        >
                          {order.orderNumber}
                        </Link>
                      </div>
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        {order.dealer}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {order.sidemark || '-'}
                      </div>
                      <div className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        On hold since {order.holdSince ? format(new Date(order.holdSince), 'MMM d, yyyy') : 'Unknown'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          setExpandedShortageOrder(
                            expandedShortageOrder === order.orderId ? null : order.orderId
                          )
                        }
                        className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors"
                      >
                        {expandedShortageOrder === order.orderId ? 'Hide' : 'Show'} Items ({order.missingItems?.length || 0})
                      </button>
                      <button
                        onClick={() => handleResolveShortage(order.orderId)}
                        disabled={resolvingOrderId === order.orderId}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50 transition-colors flex items-center gap-1"
                      >
                        {resolvingOrderId === order.orderId ? (
                          <>
                            <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Resolving...
                          </>
                        ) : (
                          <>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Resolve
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Missing Items */}
                  {expandedShortageOrder === order.orderId && order.missingItems && order.missingItems.length > 0 && (
                    <div className="border-t border-red-200 dark:border-red-800">
                      <table className="min-w-full divide-y divide-red-200 dark:divide-red-800">
                        <thead className="bg-red-100/50 dark:bg-red-900/20">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-red-700 dark:text-red-400 uppercase">
                              Part Number
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-red-700 dark:text-red-400 uppercase">
                              Description
                            </th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-red-700 dark:text-red-400 uppercase">
                              Qty Needed
                            </th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-red-700 dark:text-red-400 uppercase">
                              Qty Short
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-red-700 dark:text-red-400 uppercase">
                              Lead Time
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-red-100 dark:divide-red-900/50">
                          {order.missingItems.map((item, idx) => (
                            <tr key={idx} className="bg-white/50 dark:bg-gray-900/30">
                              <td className="px-4 py-2 whitespace-nowrap text-sm font-mono font-medium text-gray-900 dark:text-white">
                                {item.partNumber}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                                {item.description}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                                {item.qtyNeeded}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-right font-semibold text-red-600 dark:text-red-400">
                                {item.qtyShort}
                              </td>
                              <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {item.leadTime || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      ) : activeTab === 'orders' ? (
        /* Orders in Production Section */
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Orders Currently in Production</h3>
          {ordersInProduction.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No orders currently in production</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dealer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sidemark</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {ordersInProduction.map(order => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Link to={`/orders/${order.id}`} className="text-blue-600 hover:text-blue-800 font-medium">
                          {order.orderNumber}
                        </Link>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {order.companyName || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {order.sidemark || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <span className="text-gray-900">{order.completedItems}</span>
                        <span className="text-gray-400"> / {order.totalItems}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-3 mr-2">
                            <div
                              className="h-3 rounded-full transition-all duration-500"
                              style={{
                                width: `${order.progress}%`,
                                background: order.progress === 100
                                  ? '#22c55e'
                                  : 'linear-gradient(90deg, #3b82f6, #22c55e)',
                              }}
                            />
                          </div>
                          <span className="text-sm text-gray-600 dark:text-gray-300">{order.progress}%</span>
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          {order.inProgressWorkOrders > 0 && (
                            <span className="text-blue-600">{order.inProgressWorkOrders} in progress</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {order.createdAt ? format(new Date(order.createdAt), 'MMM d, yyyy') : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <Link
                          to={`/orders/${order.id}`}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          View Details
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : (
        /* Workstation Queues Section */
        <div className="space-y-6">
          {filteredQueues.map(queue => (
            <WorkstationQueueCard
              key={queue.workstationId}
              queue={queue}
              onStartWork={handleStartWork}
              onCompleteStep={handleCompleteStep}
              onBlock={(item) => {
                setSelectedItem(item);
                setShowBlockModal(true);
              }}
              onUnblock={handleUnblock}
              onPrintLabel={handlePrintLabel}
              actionLoading={actionLoading}
            />
          ))}
          {filteredQueues.length === 0 && (
            <Card>
              <p className="text-gray-500 text-center py-8">No workstation queues available</p>
            </Card>
          )}
        </div>
      )}

      {/* Block Modal */}
      {showBlockModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Block Work Order</h3>
            <p className="text-gray-600 mb-4">
              Blocking {selectedItem.workOrderNumber} - {selectedItem.stepName}
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason
                </label>
                <select
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value as BlockerType)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {BLOCKER_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note (optional)
                </label>
                <textarea
                  value={blockNote}
                  onChange={(e) => setBlockNote(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Add details about the block..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowBlockModal(false);
                  setSelectedItem(null);
                  setBlockNote('');
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleBlockItem}
                disabled={actionLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {actionLoading ? 'Blocking...' : 'Block'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function WorkstationQueueCard({
  queue,
  onStartWork,
  onCompleteStep,
  onBlock,
  onUnblock,
  onPrintLabel,
  actionLoading,
}: {
  queue: WorkstationQueue;
  onStartWork: (item: WorkOrderQueueItem) => void;
  onCompleteStep: (item: WorkOrderQueueItem) => void;
  onBlock: (item: WorkOrderQueueItem) => void;
  onUnblock: (item: WorkOrderQueueItem) => void;
  onPrintLabel: (item: WorkOrderQueueItem) => void;
  actionLoading: boolean;
}) {
  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{queue.workstationName}</h3>
          <div className="flex items-center space-x-4 mt-1 text-sm">
            <span className="text-gray-500">
              Coming up: <span className="font-medium text-gray-900">{queue.stats.comingUp}</span>
            </span>
            <span className="text-green-600">
              Ready: <span className="font-medium">{queue.stats.readyToStart}</span>
            </span>
            <span className="text-blue-600">
              In Progress: <span className="font-medium">{queue.stats.inProgress}</span>
            </span>
            {queue.stats.blocked > 0 && (
              <span className="text-red-600">
                Blocked: <span className="font-medium">{queue.stats.blocked}</span>
              </span>
            )}
            {queue.stats.late && queue.stats.late > 0 && (
              <span className="text-orange-600">
                Late: <span className="font-medium">{queue.stats.late}</span>
              </span>
            )}
          </div>
        </div>
      </div>

      {queue.items.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No items in queue</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Work Order</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Step</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {queue.items.map(item => (
                <tr key={`${item.workOrderId}-${item.stepName}`} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.workOrderNumber}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-blue-600">
                    {item.orderNumber}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    <div>{item.customerName}</div>
                    <div className="text-gray-500 text-xs">{item.sidemark}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {item.stepName}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <StatusBadge status={item.status} type="workorder" />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {item.dueDate ? format(new Date(item.dueDate), 'MMM d') : '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <div className="flex items-center space-x-2">
                      {item.status === 'ready_to_start' && (
                        <button
                          onClick={() => onStartWork(item)}
                          disabled={actionLoading}
                          className="text-green-600 hover:text-green-800 disabled:opacity-50"
                        >
                          Start
                        </button>
                      )}
                      {item.status === 'in_progress' && (
                        <button
                          onClick={() => onCompleteStep(item)}
                          disabled={actionLoading}
                          className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                        >
                          Complete
                        </button>
                      )}
                      {item.status === 'blocked' ? (
                        <button
                          onClick={() => onUnblock(item)}
                          disabled={actionLoading}
                          className="text-green-600 hover:text-green-800 disabled:opacity-50"
                        >
                          Unblock
                        </button>
                      ) : (
                        <button
                          onClick={() => onBlock(item)}
                          disabled={actionLoading}
                          className="text-red-600 hover:text-red-800 disabled:opacity-50"
                        >
                          Block
                        </button>
                      )}
                      <button
                        onClick={() => onPrintLabel(item)}
                        className="text-gray-600 hover:text-gray-800"
                      >
                        Label
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
